/**
 * F43 (Track B) — user suspend / restore drawer.
 *
 * Suspend: mandatory reason Textarea + an F26 notification preview + a
 * destructive confirm Dialog → `onSubmit({ reason })`.
 * Restore: a confirm + notification preview → `onSubmit({})`.
 *
 * Presentation-only: the detail page passes `onSubmit` wired to
 * `adminUsersApi.suspend` / `.restore` (with the If-Match header) and owns the
 * toast + refetch + the 409 STALE_USER stale-dialog. Mirrors F42's SuspendDrawer.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, Dialog, Drawer, FormField, Textarea } from '@bravemove/ui-web';
import { SUSPENSION_REASON_MAX } from '@bravemove/types';

export type UserSuspendMode = 'suspend' | 'restore';

export interface UserSuspendDrawerProps {
  readonly open: boolean;
  readonly mode: UserSuspendMode;
  readonly displayName: string;
  readonly onClose: () => void;
  /** Suspend → `{ reason }`; restore → `{}`. */
  readonly onSubmit: (input: { reason?: string }) => Promise<void>;
}

export function UserSuspendDrawer({
  open,
  mode,
  displayName,
  onClose,
  onSubmit,
}: UserSuspendDrawerProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect -- reset transient state when re-opened */
  useEffect(() => {
    if (!open) return;
    setReason('');
    setConfirmOpen(false);
    setSubmitting(false);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const isSuspend = mode === 'suspend';
  const trimmed = reason.trim();
  const reasonValid = !isSuspend || trimmed.length >= 1;

  const title = isSuspend ? t('moderation:user.suspend.title') : t('moderation:user.restore.title');

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(isSuspend ? { reason: trimmed } : {});
      setConfirmOpen(false);
      onClose();
    } catch {
      // PU-14 — the parent (`submitSuspendRestore`) has already toasted /
      // opened the stale-dialog and re-throws; swallow here so `void submit()`
      // never escapes as an unhandled rejection. The drawer stays open.
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrimary = () => {
    if (isSuspend) setConfirmOpen(true);
    else void submit();
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={title}
        footer={
          <>
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              {t('admin:f43.bulk.clear', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant={isSuspend ? 'destructive' : 'primary'}
              onClick={handlePrimary}
              loading={submitting}
              disabled={!reasonValid}
              data-testid="f43-suspend-primary"
            >
              {isSuspend
                ? t('moderation:user.suspend.confirm')
                : t('moderation:user.restore.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-4" data-testid="f43-suspend-drawer">
          <p className="text-[13px] text-ink-2">{displayName}</p>

          {isSuspend ? (
            <FormField label={t('moderation:user.suspend.reason_label')}>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={SUSPENSION_REASON_MAX}
                placeholder={t('moderation:user.suspend.reason_placeholder')}
                rows={4}
                data-testid="f43-suspend-reason"
              />
            </FormField>
          ) : null}

          <Banner tone={isSuspend ? 'warning' : 'info'} data-testid="f43-suspend-warning">
            {isSuspend
              ? t('moderation:user.suspend.warning')
              : t('moderation:user.restore.warning')}
          </Banner>

          {/* F26 notification preview */}
          <div
            className="rounded-md border border-outline bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2"
            data-testid="f43-suspend-notification-preview"
          >
            {isSuspend
              ? t('moderation:user.suspend.notification_preview')
              : t('moderation:user.restore.notification_preview')}
          </div>
        </div>
      </Drawer>

      {isSuspend ? (
        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => void submit()}
          destructive
          title={t('moderation:user.suspend.title')}
          description={t('moderation:user.suspend.warning')}
          confirmLabel={t('moderation:user.suspend.confirm')}
          loading={submitting}
        />
      ) : null}
    </>
  );
}
